import { useCallback, useState } from 'react';
import moment from 'moment-timezone';
import { sortBy } from 'lodash';

import useRequestJSON, {
  RequestResult,
  useRequestOnEvent
} from './hooks/useRequestJSON';
import {
  PostJSON,
  BaseResponse,
  PaginatedParams,
  ErrorResponse,
  FetchSegment
} from './request';
import type { TriageData, TriageParamsOptions } from '../contexts/triage/types';
import { usePatientGroup } from '../contexts/patient-group';

import type { IPatient } from './patient';
import type { IPatientGroup, PatientGroupFilters } from './group';
import type { AppKey } from './apps';
import { GetCurrentTZ } from './time';
import type { IContact } from './contact';

export enum AlertType {
  Call = 'C',
  Page = 'P',
  Email = 'E',
  Integration = 'I',
  Off = 'O'
}

export interface IAlert {
  id: number;
  type: AlertType;
  body: string;
  triggerSession?: number;
  triggerValue?: string;
  attemptsRemaining: number;
  isVoided: boolean;
  voidedAt?: Date;
  isRead: boolean;
  readAt?: Date;
  onHold?: boolean;
  expiresAt?: Date;
  firstSnoozedAt?: Date;
  snoozedAt?: Date;
  snoozedUntil?: Date;
  snoozedById?: number;
  unsnoozedReminder?: string;
  accountId: number;
  app: string;
  contactId: number;
  contact?: IContact;
  patient?: IPatient;
  patientId: number;
  createdAt: Date;
  deletedAt?: Date;
  updatedAt: Date;
  // Triage use only
  _justResolved?: boolean;
  _justSnoozed?: boolean;
}

/**
 * TODO: May be able to replace _justResolved or _justSnoozed if TriageProvider updates instantly
 */
export const IsAlertResolved = (alert: IAlert): boolean => {
  return (
    alert.isRead && typeof alert.readAt !== 'undefined' && alert.readAt !== null
  );
};
export const IsAlertSnoozed = (alert: IAlert): boolean => {
  return (
    alert._justSnoozed ||
    (!alert.isRead &&
      typeof alert.snoozedUntil !== 'undefined' &&
      alert.snoozedAt !== null &&
      moment(alert.snoozedUntil) > moment())
  );
};

// Hooks

interface GetAlertResponse extends BaseResponse {
  alert?: IAlert;
}

export const useGetAlert = (id: number): Omit<GetAlertResponse, 'status'> => {
  const request = useCallback(
    () =>
      PostJSON<GetAlertResponse>({
        url: '/alert/data_single/',
        params: { id }
      }),
    [id]
  );
  const { data } = useRequestJSON<GetAlertResponse>({
    request,
    enabled: id > 0
  });
  return {
    alert: data?.alert
  };
};

interface AlertCountResponse extends BaseResponse {
  total: number;
}

interface AlertCountParams {
  unreadOnly?: boolean;
  snoozedOnly?: boolean;
  patientIds?: number[];
  apps?: AppKey[];
}

const transform = (response: AlertCountResponse): number => {
  return response.total;
};

/**
 * Gets /alert/data_count/ response
 * Relies on data from active group and from triage data
 *
 * @param activeGroup active selected patient group
 * @param triageData triage data
 * @param loading used to determine when to run this effect
 * @param type should either be 'unreadOnly' or 'snoozedOnly'
 */
export const useAlertCount = (
  activeGroup: IPatientGroup,
  triageData: TriageData,
  loading: boolean,
  type: string
): RequestResult<number> => {
  const activeGroupId: number = activeGroup ? activeGroup.id : null;
  const params: AlertCountParams = {
    [type]: true
  };
  const patientIds: number[] = triageData?.patientIds || [];
  if (patientIds != null) {
    params.patientIds = patientIds;
  }
  if (
    activeGroup &&
    activeGroup.filters.apps &&
    activeGroup.filters.apps.length
  ) {
    params.apps = activeGroup.filters.apps;
  }
  const patientIdsOperand =
    patientIds == null ? null : JSON.stringify(sortBy(patientIds));
  const request = useCallback(
    () =>
      PostJSON<AlertCountResponse, AlertCountParams>({
        url: '/alert/data_count/',
        params
      }),
    [activeGroupId, patientIdsOperand]
  );
  return useRequestJSON<AlertCountResponse, number>({
    request,
    transformFn: transform,
    enabled: !loading
  });
};

export interface AlertDataResponse extends BaseResponse {
  total: number;
  alerts: IAlert[];
}

export type AlertDataValue = Omit<AlertDataResponse, 'status'>;

interface GetStaleAlertsParams extends PaginatedParams {
  unreadOnly: boolean;
  patientIds?: number[];
  apps?: AppKey[];
  tz: string;
  end?: string;
  excludedPatientIds?: number[];
  contactIds?: number[];
  tagIds?: number[];
}

interface GetStaleAlertsValue {
  page: number;
  data: AlertDataValue;
  setPage: (page: number) => void;
  isLoading: boolean;
}

const transformStaleAlerts = (
  response: AlertDataResponse
): Omit<AlertDataResponse, 'status'> => {
  return {
    total: response.total,
    alerts: response.alerts
  };
};

/**
 * Gets /alert/data response
 * Relies on data from the active patient group and triage data
 *
 * @param triageData triage data
 * @param addPatient function for adding patient to triage data
 * @param loading used to determine when to run this effect
 */
export const useGetStaleAlerts = (
  triageData: TriageData,
  loading: boolean
): GetStaleAlertsValue => {
  const [page, setPage] = useState(0);
  const { getActiveGroup } = usePatientGroup();
  const tz = GetCurrentTZ();
  const activeGroup = getActiveGroup();
  const activeGroupId: number = activeGroup ? activeGroup.id : null;
  const patientIds: number[] = triageData?.patientIds;
  const params: GetStaleAlertsParams = {
    limit: 10,
    unreadOnly: true,
    end: moment.tz(tz).subtract(2, 'weeks').endOf('day').format(),
    tz: tz,
    page: page
  };
  if (patientIds != null) {
    params.patientIds = patientIds;
  }
  if (
    activeGroup &&
    activeGroup.filters.apps &&
    activeGroup.filters.apps.length
  ) {
    params.apps = activeGroup.filters.apps;
  }
  const patientIdsOperand =
    patientIds == null ? null : JSON.stringify(sortBy(patientIds));
  const request = useCallback(
    () =>
      PostJSON<AlertDataResponse, GetStaleAlertsParams>({
        url: '/alert/data/',
        params
      }),
    [activeGroupId, patientIdsOperand, page]
  );
  const { data, isLoading } = useRequestJSON<
    AlertDataResponse,
    Omit<AlertDataResponse, 'status'>
  >({
    request,
    transformFn: transformStaleAlerts,
    enabled: !loading
  });

  return {
    data,
    page,
    setPage,
    isLoading
  };
};

/**
 * Gets /alert/data response
 * Relies on data from the active patient group and triage data
 *
 * @param triageData triage data
 * @param addPatient function for adding patient to triage data
 * @param loading used to determine when to run this effect
 */
export const useGetEndedProgramAlerts = (
  triageData: TriageData,
  loading: boolean,
  options: TriageParamsOptions,
  enabled: boolean
): GetStaleAlertsValue => {
  const [page, setPage] = useState(0);
  const { getActiveGroup } = usePatientGroup();
  const tz = GetCurrentTZ();
  const activeGroup = getActiveGroup();
  const activeGroupId: number = activeGroup ? activeGroup.id : null;
  const patientIds: number[] = triageData?.patientIds;
  const filters: PatientGroupFilters = activeGroup ? activeGroup.filters : null;
  const appFilterOptions = [];
  const params: GetStaleAlertsParams = {
    limit: 10,
    unreadOnly: true,
    tz: tz,
    page: page,
    contactIds: filters ? filters.contactIds : [],
    tagIds: filters ? filters.tagIds : []
  };
  if (patientIds != null) {
    params.excludedPatientIds = patientIds;
  }
  // Uses both options(ui app filter) and active group app filter
  // because ui app filter is triage dependent
  if (options?._tempAppFilter) {
    appFilterOptions.push(options?._tempAppFilter);
    params.apps = appFilterOptions;
  } else if (
    activeGroup &&
    activeGroup.filters.apps &&
    activeGroup.filters.apps.length
  ) {
    params.apps = activeGroup.filters.apps;
  }
  const patientIdsOperand =
    patientIds == null ? null : JSON.stringify(sortBy(patientIds));
  const request = useCallback(
    () =>
      PostJSON<AlertDataResponse, GetStaleAlertsParams>({
        url: '/alert/data/',
        params
      }),
    [activeGroupId, patientIdsOperand, page]
  );
  const { data, isLoading } = useRequestJSON<
    AlertDataResponse,
    Omit<AlertDataResponse, 'status'>
  >({
    request,
    transformFn: transformStaleAlerts,
    enabled: !loading && enabled
  });
  return {
    data,
    page,
    setPage,
    isLoading
  };
};

/**
 * useGetAlertsAnalytics
 */

export interface GetAlertsAnalyticsParams {
  start: string;
  end: string;
  tz: string;
  contactIds?: PatientGroupFilters['contactIds'];
  tagIds?: PatientGroupFilters['tagIds'];
  apps?: PatientGroupFilters['apps'];
  type?: string;
}

interface GetAlertsAnalyticsResponse extends BaseResponse {
  alerts: IAlert[];
}

interface GetAlertsAnalyticsFn {
  (
    params: GetAlertsAnalyticsParams
  ): Promise<GetAlertsAnalyticsResponse | ErrorResponse>;
}

export const GetAlertsAnalytics: GetAlertsAnalyticsFn = (params) =>
  PostJSON<GetAlertsAnalyticsResponse, GetAlertsAnalyticsParams>({
    url: '/alert/data_analytics/',
    params
  });

export const transformAlertsAnalytics = (
  response: GetAlertsAnalyticsResponse
): Omit<GetAlertsAnalyticsResponse, 'status'> => {
  return {
    alerts: response.alerts
  };
};

export const useGetAlertsAnalytics = (): Omit<
  GetAlertsAnalyticsResponse,
  'status'
> &
  FetchSegment<GetAlertsAnalyticsParams> => {
  const { data, run, isLoading } = useRequestOnEvent<
    GetAlertsAnalyticsResponse,
    Omit<GetAlertsAnalyticsResponse, 'status'>
  >();

  const fetch = (params: GetAlertsAnalyticsParams) => {
    const request = () => GetAlertsAnalytics(params);
    run(request, transformAlertsAnalytics);
  };

  return {
    alerts: data ? data.alerts : undefined,
    isLoading,
    fetch
  };
};

export interface IAlertReport extends IAlert {
  resolutionReason?: string;
  resolutionNote?: string;
  resolvedByEmail?: string;
}

export interface GetAlertReportParams {
  start: string;
  end: string;
  tz: string;
  contactIds?: PatientGroupFilters['contactIds'];
  tagIds?: PatientGroupFilters['tagIds'];
  apps?: PatientGroupFilters['apps'];
  page?: number;
}

interface GetAlertReportResponse extends BaseResponse {
  alerts: IAlertReport[];
  total: number;
}

interface GetAlertReportFn {
  (
    params: GetAlertReportParams
  ): Promise<GetAlertReportResponse | ErrorResponse>;
}

export const GetAlertReport: GetAlertReportFn = (params) =>
  PostJSON<GetAlertReportResponse, GetAlertReportParams>({
    url: '/alert/data_report/',
    params
  });

export const transformAlertReport = (
  response: GetAlertReportResponse
): Omit<GetAlertReportResponse, 'status'> => {
  return {
    alerts: response.alerts,
    total: response.total
  };
};

export const useGetAlertReport = (): Omit<GetAlertReportResponse, 'status'> &
  FetchSegment<GetAlertReportParams> => {
  const { data, run, isLoading } = useRequestOnEvent<
    GetAlertReportResponse,
    Omit<GetAlertReportResponse, 'status'>
  >();

  const fetch = (params: GetAlertReportParams) => {
    const request = () => GetAlertReport(params);
    run(request, transformAlertReport);
  };

  return {
    alerts: data ? data.alerts : undefined,
    total: data ? data.total : undefined,
    isLoading,
    fetch
  };
};
